<template>
  <Teleport :to="`.toast-content-container.${vm.position}`" v-if="vm.show">
    <div
      :id="vm.id"
      class="custom-toast"
      :class="[vm.variant, { 'snack-bar': vm.snackBar }]"
      @mouseover="pauseAutoClose"
      @mouseleave="playAutoClose"
      ref="toast"
      data-cy="toast">
      <slot name="custom-toast-header">
        <div class="custom-toast-header">
          <Icon :icon="vm.titleIcon" v-if="vm.titleIcon" />
          <div class="custom-toast-title">
            <div class="title-text">
              <rendered-title />
            </div>
          </div>
          <div class="custom-toast-close" data-cy="toast-close-btn" @click="close">
            <Icon icon="Close"></Icon>
            <svg class="vanish-circle" width="22px" height="22px" viewBox="0 0 20 20" v-if="vm.autoClose">
              <circle cx="10" cy="10" r="9"></circle>
              <path
                d="M10,1 L10,1 L10,1 C14.9705627,1 19,5.02943725 19,10 L19,10 L19,10 C19,14.9705627 14.9705627,19 10,19 L10,19 L10,19 C5.02943725,19 1,14.9705627 1,10 L1,10 L1,10 C1,5.02943725 5.02943725,1 10,1 L10,1 Z"
                class="outer"
                :style="{ 'stroke-dashoffset': 57 - 57 * (vm.timeRemainingInMS / vm.autoCloseTimeInSec) }"></path>
            </svg>
          </div>
        </div>
      </slot>
      <template v-if="!vm.snackBar">
        <div class="custom-toast-body" data-cy="custom-toast-body">
          <slot>
            <div class="custom-toast-body-icon-area" v-if="vm.textIllustration">
              <svg class="icon-background" width="78" height="126" viewBox="0 0 78 126" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M57.5 0H0V126H27L78 75L57.5 0Z" fill="#F1F1F1" />
              </svg>
              <div class="icon-illustration">
                <Icon :icon="vm.textIllustration" size="32px" />
                <div class="icon-illustration-status" v-if="vm.statusTextIcon">
                  <Icon :icon="vm.statusTextIcon" :variant="vm.variant" />
                </div>
              </div>
            </div>
            <div class="custom-toast-body-text-area">
              <div class="text-area-content" :class="{ 'with-button-area': showActiopnArea }">
                <rendered-text />
              </div>
              <div class="text-area-actions" v-if="showActiopnArea">
                <slot name="primaryAction">
                  <button class="btn mono" data-cy="toast-primary-action-btn" @click="primaryActionClick" v-if="vm.primaryActionButton">
                    {{ vm.primaryActionButton }}
                  </button>
                </slot>
                <slot name="primaryAction">
                  <button class="btn mono" data-cy="toast-secondary-action-btn" @click="secondaryActionClick" v-if="vm.secondaryActionButton">
                    {{ vm.secondaryActionButton }}
                  </button>
                </slot>
              </div>
            </div>
          </slot>
        </div>
      </template>
    </div>
  </Teleport>
</template>
<script setup lang="ts">
import { createContainer, removeEmptyContainer } from "@common/composable/toast";
import { ColorVariants } from "@medapro-types/common/common";
import { ToastOptions, ToastPositions, ToastProps } from "@medapro-types/common/components/Toast";
import emitter from "tiny-emitter/instance";
import { VNode, computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch, withDefaults } from "vue";
import Icon from "./Icon.vue";
//#region General
const props = withDefaults(defineProps<ToastProps>(), {
  autoClose: true,
  autoCloseTimeInSec: 5,
  text: "Text",
  title: "Title",
  variant: "primary",
  snackBar: false
});

const emit = defineEmits(["primaryAction", "secondaryAction", "input"]);

const toast = ref<HTMLElement | null>(null);
//#endregion

//#region ViewModel
type ViewModel = {
  autoCloseTimeInSec: number;
  timeRemainingInMS: number;
  pausedAutoClose: boolean;
  show: boolean;
  autoClose: boolean;
  text: string | VNode | Array<VNode | string>;
  title: string;
  titleIcon: string | undefined;
  textIllustration?: string | undefined;
  statusTextIcon?: string | undefined;
  variant: ColorVariants;
  position: ToastPositions;
  primaryActionButton: string | undefined;
  secondaryActionButton: string | undefined;
  primaryAction: undefined | (() => void);
  secondaryAction: undefined | (() => void);
  id: string | undefined;
  textIsVNode: boolean;
  snackBar: boolean;
};
const vm = reactive<ViewModel>({
  timeRemainingInMS: props.autoCloseTimeInSec * 1000,
  autoCloseTimeInSec: props.autoCloseTimeInSec * 1000,
  pausedAutoClose: false,
  show: props.value,
  autoClose: props.autoClose,
  text: props.text,
  title: props.title,
  titleIcon: props.titleIcon,
  textIllustration: props.textIllustration,
  statusTextIcon: props.statusTextIcon,
  variant: props.variant,
  position: props.position ?? (props.snackBar ? "bottom" : "top-right"),
  primaryActionButton: props.primaryActionButton,
  secondaryActionButton: props.secondaryActionButton,
  primaryAction: undefined,
  secondaryAction: undefined,
  id: props.id,
  textIsVNode: false,
  snackBar: props.snackBar
});
//#endregion

//#region Show

const renderedText = () => {
  return vm.text;
};
const renderedTitle = () => {
  return vm.title;
};

const showFunction = (toastOptions: ToastOptions) => {
  Object.assign(vm, toastOptions);
  createContainer(vm.position);
  vm.show = true;
  if (vm.autoClose) triggerClosingAfterSeconds(vm.autoCloseTimeInSec);
};

async function close() {
  removeAllIntervals();
  vm.timeRemainingInMS = vm.autoCloseTimeInSec;
  vm.show = false;
  await nextTick();
  removeEmptyContainer(vm.position);
}

const closeById = async (args: { id: string }) => {
  if (args.id == vm.id) {
    close();
  }
};

const getMilliSecondfromTimeString = (s: string): number | undefined => {
  const regExp = new RegExp("(\\d+)(\\w+)");
  const result = s.match(regExp);
  if (!result) return;
  let duration = parseInt(result[1]);
  switch (result[2]) {
    case "s":
      duration *= 1000;
      break;
    default:
      break;
  }
  return duration;
};

const addAnimationClass = () => {
  const container = createContainer(vm.position);
  if (container && toast.value) {
    container.classList.remove("anim-stop");
    container.classList.add("anim-start");
    const animationLength = getMilliSecondfromTimeString(getComputedStyle(toast.value).animationDuration) ?? 0;
    setTimeout(() => {
      container.classList.remove("anim-start");
      container.classList.add("anim-stop");
    }, animationLength);
  }
};
//#endregion

//#region AutoClose

let timeHelperEnd: number;
const intervals: NodeJS.Timeout[] = [];

const triggerClosingAfterSeconds = (milliseconds: number) => {
  timeHelperEnd = Date.now() + milliseconds;
  vm.timeRemainingInMS = milliseconds;
  intervals.push(
    setInterval(() => {
      vm.timeRemainingInMS = timeHelperEnd - Date.now();
      timeHelperEnd = Date.now() + vm.timeRemainingInMS;
      if (vm.timeRemainingInMS <= 0) {
        timeHelperEnd = 0;
        close();
      }
    }, 50)
  );
};

const pauseAutoClose = () => {
  if (vm.autoClose) {
    removeAllIntervals();
  }
};

const playAutoClose = () => {
  if (vm.autoClose) {
    if (!intervals?.length) {
      triggerClosingAfterSeconds(vm.timeRemainingInMS);
    }
  }
};

const removeAllIntervals = () => {
  while (intervals.length) {
    clearInterval(intervals.pop());
  }
};

//#endregion

//#region Actions
const showActiopnArea = computed(() => {
  return !!vm.primaryActionButton || !!vm.secondaryActionButton;
});

const primaryActionClick = () => {
  if (vm.primaryAction) {
    vm.primaryAction();
  } else {
    emit("primaryAction");
  }
};
const secondaryActionClick = () => {
  if (vm.secondaryAction) {
    vm.secondaryAction();
  } else {
    emit("secondaryAction");
  }
};
//#endregion

//#region LifeCycle

watch(() => toast.value, addAnimationClass);

watch(
  () => props.value,
  async (nv) => {
    if (nv) await showFunction(vm);
    else close();
  }
);

watch(
  () => vm.show,
  (nv) => {
    emit("input", nv);
  },
  { immediate: true }
);

onMounted(() => {
  emitter.on("close-toast-by-id", closeById);
});

onBeforeUnmount(() => {
  emitter.off("close-toast-by-id", closeById);
  removeAllIntervals();
});

//#endregion

//#region Only for Expose

const getId = () => {
  return vm.id;
};

const getPosition = () => {
  return vm.position;
};
//#endregion

defineExpose({ show: showFunction, close, getId, IsClosed: !vm.show, getPosition });
</script>
